import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ExternalLink, Quote, Search } from "lucide-react";
import { useState, type FC, type KeyboardEvent } from "react";

import { AgentRobot } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { FilterChip } from "@/components/ui/filter-chip";
import { MetaLine } from "@/components/ui/meta-line";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { searchLegalSource } from "./researchApi";
import { askPrompt, citeHit, RESEARCH_SOURCES } from "./sources";
import type { ResearchHit, ResearchSourceId } from "./types";

const EXAMPLES: Record<ResearchSourceId, string[]> = {
  case_law: [
    "Matter of Dhanasar national interest waiver",
    "Particular social group firm resettlement"
  ],
  unpublished: [
    "Judging the work of others O-1A",
    "Critical role for a distinguished organization"
  ],
  statutes: ["INA 245 adjustment of status", "8 CFR 204.5(h)"],
  references: ["O-1 comparable evidence", "National interest waiver"]
};

const errorText = (error: unknown): string => {
  if (isAxiosError<{ error?: string }>(error)) {
    if (error.response?.status === 503)
      return "This source isn't configured on this server.";
    return error.response?.data?.error ?? "The search didn't go through.";
  }
  return "The search didn't go through.";
};

/** Kept by the popover so a closed panel reopens where it was. */
type SearchState = {
  sourceId: ResearchSourceId;
  draft: string;
  submitted: string;
};

type PanelProps = {
  state: SearchState;
  onStateChange: (next: SearchState) => void;
  agentLabel: string;
  onCite: (hit: ResearchHit) => void;
  onAsk: (prompt: string) => void;
};

/** The open panel. Mounted only while open, so a closed one never fetches. */
const ResearchPanel: FC<PanelProps> = ({
  state,
  onStateChange,
  agentLabel,
  onCite,
  onAsk
}) => {
  const { sourceId, draft, submitted } = state;
  const source =
    RESEARCH_SOURCES.find((s) => s.id === sourceId) ?? RESEARCH_SOURCES[0];

  const search = useQuery({
    queryKey: ["research-legal", sourceId, submitted],
    queryFn: () => searchLegalSource(sourceId, submitted),
    enabled: submitted.length > 0,
    staleTime: 5 * 60_000,
    retry: false
  });
  const results = search.data?.results ?? [];

  const run = (text: string) =>
    onStateChange({ ...state, draft: text, submitted: text.trim() });
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // The popover is portaled out of the composer's form, but React events
    // still bubble to it.
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      run(draft);
    }
  };
  const ask = () => {
    const query = draft.trim() || submitted;
    if (query) onAsk(askPrompt(source, query));
  };

  return (
    <>
      <div className="flex items-center gap-2 border-b border-line-1 px-3">
        <Search className="size-3.5 shrink-0 text-fg-3" />
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          value={draft}
          onChange={(e) =>
            onStateChange({ ...state, draft: e.currentTarget.value })
          }
          onKeyDown={onKeyDown}
          placeholder={`Search ${source.label}…`}
          aria-label="Research Query"
          data-testid="research-input"
          className="h-10 min-w-0 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-4"
        />
        {search.isFetching ? <Spinner className="size-3.5 text-fg-3" /> : null}
      </div>
      <div
        className="flex gap-1 overflow-x-auto px-3 py-2 [scrollbar-width:none]"
        role="group"
        aria-label="Source"
      >
        {RESEARCH_SOURCES.map((s) => (
          <FilterChip
            key={s.id}
            active={s.id === sourceId}
            onClick={() => onStateChange({ ...state, sourceId: s.id })}
            data-testid={`research-source-${s.id}`}
          >
            {s.label}
          </FilterChip>
        ))}
      </div>

      <div className="max-h-80 min-h-24 overflow-y-auto border-t border-line-1 [scrollbar-width:thin]">
        {!submitted ? (
          <div className="flex flex-col gap-1 px-3 py-2.5">
            <span className="text-sm text-fg-3">Try</span>
            {EXAMPLES[sourceId].map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => run(example)}
                className="-mx-1.5 rounded-md px-1.5 py-1 text-left text-base text-fg-2 hover:bg-fill-1 hover:text-fg"
              >
                {example}
              </button>
            ))}
          </div>
        ) : search.isError ? (
          <div className="flex flex-col items-start gap-2 px-3 py-3 text-base text-fg-2">
            {errorText(search.error)}
            <Button size="xs" variant="secondary" onClick={ask}>
              <AgentRobot state="static" className="size-3.5" />
              Ask {agentLabel} to Search Instead
            </Button>
          </div>
        ) : search.isPending ? (
          <div className="px-3 py-3 text-base text-fg-3">
            Searching {source.label}…
          </div>
        ) : results.length === 0 ? (
          <div className="px-3 py-3 text-base text-fg-3">
            Nothing in {source.label} for “{submitted}”.
          </div>
        ) : (
          <ul data-testid="research-results">
            {search.data?.note ? (
              <li className="px-3 pt-2 text-sm text-warn">
                {search.data.note}
              </li>
            ) : null}
            {results.map((hit, i) => (
              <li
                key={`${hit.title}-${i}`}
                className="group flex flex-col gap-0.5 px-3 py-2 hover:bg-fill-1"
              >
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1 truncate text-base font-medium text-fg">
                    {hit.title}
                  </span>
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => onCite(hit)}
                      data-testid="research-cite"
                    >
                      <Quote />
                      Cite
                    </Button>
                    {hit.url ? (
                      <Button size="icon-xs" variant="ghost" asChild>
                        <a
                          href={hit.url}
                          target="_blank"
                          rel="noreferrer"
                          aria-label="Open Source"
                        >
                          <ExternalLink className="size-3.5" />
                        </a>
                      </Button>
                    ) : null}
                  </div>
                </div>
                <MetaLine
                  parts={[
                    hit.citation !== hit.title ? hit.citation : null,
                    hit.date,
                    ...hit.tags
                  ]}
                />
                {hit.snippet ? (
                  <p className="line-clamp-2 text-sm text-fg-3">
                    {hit.snippet}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-line-1 py-2 pr-2 pl-3">
        <span className="min-w-0 flex-1 truncate text-sm text-fg-3">
          {source.authority}
        </span>
        <Button
          size="sm"
          variant="secondary"
          disabled={!draft.trim() && !submitted}
          onClick={ask}
          data-testid="research-ask"
        >
          <AgentRobot state="static" className="size-3.5" />
          Ask {agentLabel}
        </Button>
      </div>
    </>
  );
};

type Props = {
  disabled?: boolean;
  agentLabel: string;
  /** Adds a citation to what's being typed. */
  onCite: (text: string) => void;
  /** Replaces the composer's text with a research prompt for the agent. */
  onAsk: (prompt: string) => void;
};

/**
 * The composer's Research control: search the immigration library (published
 * decisions, unpublished decisions, statutes, and manuals) directly, cite a
 * hit into the message, or hand the question to the agent.
 */
export const ResearchPopover: FC<Props> = ({
  disabled,
  agentLabel,
  onCite,
  onAsk
}) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<SearchState>({
    sourceId: "case_law",
    draft: "",
    submitted: ""
  });
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="pill"
              size="xs"
              className="gap-1 px-2"
              aria-label="Research"
              disabled={disabled}
              data-testid="composer-research"
            >
              <Search className="size-3.5" />
              Research
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          Search the immigration library
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="top"
        align="start"
        className="w-md gap-0 p-0"
        data-testid="research-popover"
        // The composer takes focus back after a cite or ask.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ResearchPanel
          state={state}
          onStateChange={setState}
          agentLabel={agentLabel}
          onCite={(hit) => {
            setOpen(false);
            onCite(citeHit(hit));
          }}
          onAsk={(prompt) => {
            setOpen(false);
            onAsk(prompt);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
